// src/features/imports/components/PdfExtractionPanel.jsx
//
// INGEST-LEASE-PDF-S4: Map step panel — shown in ImportSessionReview after
// a successful pdf_abstraction upload.
//
// Displays the extracted fields from the LLM with confidence badges,
// citation references, and a bucket split:
//   "Ready" (high/medium confidence, non-null)
//   "Needs your eyes" (low confidence or null)
//
// Props:
//   extractedFields  Array<{ fieldKey, value, confidence, citation }>
//   collapsible      boolean — whether to show the collapse toggle (default true)
//   defaultOpen      boolean — whether to start expanded (default true)

import React, { useState } from "react";
import { T } from "../../../app/tokens.js";

// ─── Confidence badge ─────────────────────────────────────────────────────────

const CONF_STYLE = {
  high:   { bg: T.utilGreen,   fg: T.utilGreenText, label: "HIGH"   },
  medium: { bg: T.warnAmberBg, fg: T.amberDark,     label: "MEDIUM" },
  low:    { bg: T.ragRedBg,    fg: T.ragRed,        label: "LOW"    },
};

function ConfBadge({ confidence }) {
  const s = CONF_STYLE[confidence] || CONF_STYLE.low;
  return (
    <span
      data-testid={`pdf-conf-badge-${confidence}`}
      style={{
        fontSize: T.fsXxs, fontWeight: T.fwBold,
        padding: "1px 6px", borderRadius: 4,
        background: s.bg, color: s.fg,
        flexShrink: 0, whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  );
}

// ─── Field label map (reuse DiffRow conventions) ─────────────────────────────

const FIELD_LABELS = {
  lessee:                  "Lessee",
  vendor:                  "Vendor / Landlord",
  startDate:               "Start date",
  endDate:                 "End date",
  size:                    "Size",
  areaType:                "Area type",
  currency:                "Currency",
  type:                    "Lease type",
  opexStructure:           "OpEx structure",
  freeRentMonths:          "Free rent (months)",
  tiaTotalAmount:          "TIA total",
  securityDepositAmount:   "Security deposit",
  // LEASE-FIELDS-ADD-DROP: securityDepositType removed (column dropped in 0216)
  // 4 new Spacebase fields added (migration 0216)
  possessionDate:          "Possession date",
  lessorContact:           "Lessor contact",
  rentPaymentType:         "Rent payment type",
  termType:                "Term type",
  renewalCount:            "Renewal count",
  renewalTermYears:        "Renewal term (years)",
  renewalNoticeMonths:     "Renewal notice (months)",
  breakDate:               "Break date",
  breakNoticeMonths:       "Break notice (months)",
  expansionOptionNotes:    "Expansion option notes",
  opexCapPercent:          "OpEx cap %",
  baseYear:                "Base year",
  baseYearOpex:            "Base year OpEx",
  baseYearTax:             "Base year tax",
  // LEASE-FIELDS-ADD-DROP: camDescription removed (column dropped in 0216)
  escalationType:          "Escalation type",
  escalationRate:          "Escalation rate",
  // LEASE-FIELDS-ADD-DROP: holdoverPercent removed (column dropped in 0216)
  holdoverPeriodMonths:    "Holdover period (months)",
  permittedUse:            "Permitted use",
  noticeAddress:           "Notice address",
  subletConsentRequired:   "Sublease consent required",
  subletProfitSharePercent:"Sublease profit share %",
  restorationObligations:  "Restoration obligations",
  rofrRofoNotes:           "ROFR / ROFO notes",
  notes:                   "Notes",
  yearCosts:               "Annual rent schedule",
};

function fieldLabel(key) {
  return FIELD_LABELS[key] || key;
}

function fmtValue(v) {
  if (v === null || v === undefined) return null;
  if (Array.isArray(v)) return `${v.length} year(s)`;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

// ─── Single field row ─────────────────────────────────────────────────────────

function FieldRow({ field }) {
  const display = fmtValue(field.value);
  const isEmpty = display === null;

  return (
    <div
      data-testid={`pdf-field-row-${field.fieldKey}`}
      style={{
        display: "flex", alignItems: "flex-start", gap: 8,
        padding: "6px 0",
        borderBottom: `1px solid ${T.border}`,
      }}
    >
      <div style={{ flex: "0 0 200px", fontSize: T.fsSm, color: T.mid, paddingTop: 1 }}>
        {fieldLabel(field.fieldKey)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {isEmpty ? (
          <span style={{ fontSize: T.fsSm, color: T.light, fontStyle: "italic" }}>
 - not extracted - 
          </span>
        ) : (
          <span
            style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark }}
            title={display}
          >
            {display}
          </span>
        )}
        {(field.citation || field.documentName) && (
          <div style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 2, fontStyle: "italic" }}>
            {field.citation && <span>📄 {field.citation}</span>}
            {field.documentName && (
              <span
                data-testid={`pdf-doc-source-${field.fieldKey}`}
                style={{ marginLeft: field.citation ? 6 : 0 }}
              >
                {field.citation && "· "}from {field.documentName}
                {field.documentDate && `, ${field.documentDate}`}
              </span>
            )}
          </div>
        )}
        {field.conflictNote && (
          <div
            data-testid={`pdf-conflict-note-${field.fieldKey}`}
            style={{
              fontSize: T.fsXxs, color: T.warnAmberText,
              marginTop: 2, fontStyle: "italic",
              background: T.warnAmberBg, borderRadius: 3,
              padding: "1px 5px", display: "inline-block",
            }}
          >
            ⚠ {field.conflictNote}
          </div>
        )}
      </div>
      <div style={{ flexShrink: 0 }}>
        <ConfBadge confidence={field.confidence || "low"} />
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function PdfExtractionPanel({
  extractedFields = [],
  collapsible = true,
  defaultOpen = true,
}) {
  const [open, setOpen] = useState(defaultOpen);

  if (!extractedFields || extractedFields.length === 0) return null;

  const ready = extractedFields.filter(
    (f) => f.value !== null && f.value !== undefined && (f.confidence === "high" || f.confidence === "medium")
  );
  const needsReview = extractedFields.filter(
    (f) => f.value === null || f.value === undefined || f.confidence === "low"
  );

  return (
    <div
      data-testid="pdf-extraction-panel"
      style={{
        border: `1px solid ${T.spacebaseBlue}30`,
        borderRadius: 8,
        marginBottom: 12,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "10px 14px",
          background: `${T.spacebaseBlue}0A`,
          borderBottom: open ? `1px solid ${T.spacebaseBlue}30` : "none",
        }}
      >
        <span style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark, flex: 1 }}>
          🔍 PDF Extraction Map
        </span>
        <span style={{ fontSize: T.fsXs, color: T.mid }}>
          {ready.length} ready · {needsReview.length} need{needsReview.length === 1 ? "s" : ""} review
        </span>
        {collapsible && (
          <button
            type="button"
            data-testid="pdf-panel-toggle"
            onClick={() => setOpen((p) => !p)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: T.spacebaseBlue, fontSize: T.fsSm, padding: "0 4px",
            }}
          >
            {open ? "▲ Hide" : "▼ Show"}
          </button>
        )}
      </div>

      {open && (
        <div style={{ padding: "8px 14px 4px" }}>

          {/* Ready bucket */}
          {ready.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{
                fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.utilGreenText,
                textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4,
              }}>
                Ready ({ready.length})
              </div>
              {ready.map((f) => <FieldRow key={f.fieldKey} field={f} />)}
            </div>
          )}

          {/* Needs review bucket */}
          {needsReview.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{
                fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.ragRed,
                textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4,
              }}>
                Needs your eyes ({needsReview.length})
              </div>
              {needsReview.map((f) => <FieldRow key={f.fieldKey} field={f} />)}
            </div>
          )}

          <div style={{ fontSize: T.fsXxs, color: T.mid, paddingBottom: 8, lineHeight: 1.5 }}>
            These are the LLM-extracted fields. Review the diff grid below to accept or reject individual changes.
            Fields with LOW confidence or missing citations were excluded from the diff.
          </div>
        </div>
      )}
    </div>
  );
}
